import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { Branding } from '@/types/company-config';

interface BrandingTabProps {
  data: Branding;
  onChange: (field: string, value: string) => void;
  readOnly?: boolean;
}

const BrandingTab: React.FC<BrandingTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.value);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Branding & Appearance
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Customize the look and feel of your platform to match your brand.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Primary Brand Color"
            value={data.primaryColor || '#1976d2'}
            onChange={handleChange('primaryColor')}
            type="color"
            disabled={readOnly}
            helperText="Main brand color used for buttons, headers, etc."
            InputProps={{
              style: { height: '56px' }
            }}
          />
          <Box 
            mt={1} 
            height={40} 
            bgcolor={data.primaryColor || '#1976d2'} 
            borderRadius={1}
            border="1px solid #e0e0e0"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Secondary Brand Color"
            value={data.secondaryColor || '#dc004e'}
            onChange={handleChange('secondaryColor')}
            type="color"
            disabled={readOnly}
            helperText="Secondary brand color for accents"
            InputProps={{
              style: { height: '56px' }
            }}
          />
          <Box 
            mt={1} 
            height={40} 
            bgcolor={data.secondaryColor || '#dc004e'} 
            borderRadius={1}
            border="1px solid #e0e0e0"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Favicon URL"
            value={data.favicon || ''}
            onChange={handleChange('favicon')}
            type="url"
            placeholder="https://www.example.com/favicon.ico"
            disabled={readOnly}
            helperText="URL to your favicon (small icon displayed in browser tabs)"
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Custom CSS
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add custom CSS to further customize the appearance of your platform.
            Use this carefully as it can affect the platform's functionality.
          </Typography>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Custom CSS"
            value={data.customCss || ''}
            onChange={handleChange('customCss')}
            multiline
            rows={8}
            disabled={readOnly}
            placeholder={`/* Example custom CSS */
.header-logo {
  max-height: 40px;
}

.custom-button {
  background-color: var(--primary-color);
  border-radius: 8px;
}`}
            helperText="Advanced: Custom CSS will be injected into the platform. Test thoroughly before applying."
          />
        </Grid>

        <Grid size={12}>
          <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Preview Note:</strong> Branding changes will be applied after saving and may require 
              a page refresh to take full effect. The primary color will be used for navigation elements, 
              buttons, and interactive components throughout the platform.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrandingTab;